import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import TableContainer from "components/Common/DataTableContainer"
import ActionDropdown from "components/ActionDropdown"
import StatsCard from "components/StatsCard"
import { FaRupeeSign } from "react-icons/fa"
import { getAllInvoicesRequest } from "../../../redux/actions/auth"

const formatDate = date => {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, "0")
  const day = `${date.getDate()}`.padStart(2, "0")
  return `${year}-${month}-${day}`
}

const triggerDownload = (url, filename) => {
  fetch(url, { mode: "cors" })
    .then(response => response.blob())
    .then(blob => {
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = filename || "invoice"
      link.click()
      window.URL.revokeObjectURL(link.href)
    })
    .catch(err => console.error("Download failed:", err))
}

const AllInvoices = () => {
  document.title = "All Invoices"

  const dispatch = useDispatch()
  const invoicesScreen = useSelector(state => state.screens.getAllInvoices)
  const [invoiceData, setInvoiceData] = useState([])
  const startDateInputRef = useRef(null)
  const endDateInputRef = useRef(null)
  const [startDate, setStartDate] = useState(() => {
    const end = new Date()
    const start = new Date()
    start.setDate(end.getDate() - 7)
    return start
  })
  const [endDate, setEndDate] = useState(() => new Date())
  const token = useSelector(state => state.auth.token)

  const columns = useMemo(
    () => [
      {
        header: "Invoice No",
        accessorKey: "invoice_no",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Invoice Date",
        accessorKey: "invoice_date",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Invoice Type",
        accessorKey: "invoice_type",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "GST Amount",
        accessorKey: "total_gst_amount",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Grand Total",
        accessorKey: "grand_total",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: info => {
          const row = info.row.original
          if (!row.filename) {
            return <span className="text-muted">—</span>
          }
          return (
            <ActionDropdown buttonLabel="Invoice">
              <button
                className="dropdown-item"
                onClick={() => window.open(row.file_url, "_blank")}
              >
                Open
              </button>
              <button
                className="dropdown-item"
                onClick={() => triggerDownload(row.file_url, row.invoice_no)}
              >
                Download
              </button>
            </ActionDropdown>
          )
        },
      },
    ],
    []
  )

  const handleSearch = () => {
    if (!startDate || !endDate) return
    dispatch(
      getAllInvoicesRequest({
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
        action_type: "get",
      })
    )
  }

  const handleDownload = async action => {
    
    if (!startDate || !endDate) return
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/get-all-invoices`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            start_date: formatDate(startDate),
            end_date: formatDate(endDate),
            action_type: action,
          }),
        }
      )

      const data = await response.json()
      if (!response.ok || !data?.success) {
        console.error("Download failed:", data?.message || response.statusText)
        return
      }

      const downloadUrl = action === "zip" ? data.zip_url : data.fileUrl
      if (!downloadUrl) {
        console.error("Download URL missing in response")
        return
      }

      const filename = action === "zip" ? "all-invoices.zip" : "all-invoices.xlsx"
      triggerDownload(downloadUrl, filename)
    } catch (error) {
      console.error("Download failed:", error)
    }
  }

  const totals = useMemo(() => {
    const totalGst = invoiceData.reduce((sum, invoice) => {
      const value = Number.parseFloat(invoice && invoice.total_gst_amount)
      return sum + (Number.isNaN(value) ? 0 : value)
    }, 0)
    const grandTotal = invoiceData.reduce((sum, invoice) => {
      const value = Number.parseFloat(invoice && invoice.grand_total)
      return sum + (Number.isNaN(value) ? 0 : value)
    }, 0)
    return { totalGst, grandTotal }
  }, [invoiceData])

  useEffect(() => {
    handleSearch()
  }, [dispatch])

  useEffect(() => {
    const data = invoicesScreen && invoicesScreen.data
    if (data && Array.isArray(data.allInvoices)) {
      setInvoiceData(data.allInvoices)
    } else {
      setInvoiceData([])
    }
  }, [invoicesScreen])

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="mb-3 pb-2"></div>
        <h4 className="nom-title h4 mb-2">All Invoices</h4>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <StatsCard
              title="Total GST"
              value={`₹${totals.totalGst.toFixed(2)}`}
              icon={<FaRupeeSign />}
              bgClass="bg-info-soft"
            />
          </div>
          <div className="col-md-6">
            <StatsCard
              title="Grand Total"
              value={`₹${totals.grandTotal.toFixed(2)}`}
              icon={<FaRupeeSign />}
              bgClass="bg-success-soft"
            />
          </div>
        </div>
        <div className="row g-3 mb-3">
          <div
            className="col-md-4"
            onClick={() => {
              if (startDateInputRef.current) {
                if (typeof startDateInputRef.current.showPicker === "function") {
                  startDateInputRef.current.showPicker()
                } else {
                  startDateInputRef.current.focus()
                }
              }
            }}
            role="presentation"
          >
            <label className="form-label" htmlFor="invoice-start-date">
              Start Date
            </label>
            <input
              id="invoice-start-date"
              type="date"
              className="form-control"
              ref={startDateInputRef}
              value={formatDate(startDate)}
              onChange={event => {
                const value = event.target.value
                if (!value) return
                const nextDate = new Date(value)
                if (!Number.isNaN(nextDate.getTime())) {
                  setStartDate(nextDate)
                }
              }}
            />
          </div>
          <div
            className="col-md-4"
            onClick={() => {
              if (endDateInputRef.current) {
                if (typeof endDateInputRef.current.showPicker === "function") {
                  endDateInputRef.current.showPicker()
                } else {
                  endDateInputRef.current.focus()
                }
              }
            }}
            role="presentation"
          >
            <label className="form-label" htmlFor="invoice-end-date">
              End Date
            </label>
            <input
              id="invoice-end-date"
              type="date"
              className="form-control"
              ref={endDateInputRef}
              value={formatDate(endDate)}
              onChange={event => {
                const value = event.target.value
                if (!value) return
                const nextDate = new Date(value)
                if (!Number.isNaN(nextDate.getTime())) {
                  setEndDate(nextDate)
                }
              }}
            />
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={handleSearch}
            >
              Search
            </button>
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <ActionDropdown className="btn btn-success w-100" buttonLabel="Download">
              <button
                type="button"
                className="dropdown-item"
                onClick={() => handleDownload("excel")}
              >
                Excel sheet
              </button>
              <button
                type="button"
                className="dropdown-item"
                onClick={() => handleDownload("zip")}
              >
                .zip file
              </button>
            </ActionDropdown>
          </div>
          <div className="col-md-2 d-flex align-items-end"></div>
        </div>
        <TableContainer
          defaultPageSize={20}
          columns={columns}
          data={invoiceData || []}
          isGlobalFilter={true}
          isPagination={true}
          isCustomPageSize={true}
          SearchPlaceholder="Search records..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          theadClass="table-light"
          divClassName="table-responsive mb-4 nominee-tbl-wrapper"
          noDataMessage={
            invoicesScreen && invoicesScreen.error
              ? invoicesScreen.error
              : "No invoices found"
          }
        />
      </div>
    </div>
  )
}

export default AllInvoices
